import Service, { service } from '@ember/service'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type SessionService from 'teacher-ui-client/services/session'
import type { Log } from '@blakeelearning/log'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { endOfDay, startOfDay, type NormalizedInterval } from 'date-fns'

export default class RecentWritingService extends Service {
  @service authToken!: AuthToken

  @service session!: SessionService

  @service log!: Log

  get headers() {
    return {
      Authorization: `Bearer ${this.authToken.token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }
  }

  buildUrl(writingType: string): string {
    return join(config.writingLegendsApiUrl, 'v1', 'retui', 'school', this.session.currentSchool.id, writingType).href
  }

  /* Dates provided here for the period are only meant to be taken for their year/month/day values. The user only
   * selects _dates_ from the date picker (not times), and as such they should be inclusive for both start & end.
   * This should calculation should be based on the timezone the user is currently in (i.e. what the date already
   * has applied to it), _and then_ converted to an ISO string as the last step before handing to the backend to use
   * for searching events.
   *
   * For example;
   *  period.end = new Date(2014, 9, 10)  => Fri Oct 10 2014 00:00:00 GMT+1000
   *  endOfDay(period.end)                => Fri Oct 10 2014 23:59:59 GMT+1000
   *  endOfDay(period.end).toISOString()  => 2014-10-10T13:59:59.999Z
   */
  buildQueryPeriod({ start, end }: NormalizedInterval) {
    return {
      start: startOfDay(new Date(start)).toISOString(),
      end: endOfDay(new Date(end)).toISOString(),
    }
  }

  async fetchData<R>(url: string): Promise<R | undefined> {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      })

      if (response.ok) {
        return (await response.json()) as R
      } else {
        // Some unknown error. Log it.
        this.log.error(`Failed to fetch recent-writing report; Reason: (${response.status})`, url)
      }
    } catch (e: any) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else {
        this.log.error(`Failed to fetch recent-writing report; Reason: (error thrown)`, e, url)
      }
    }
    // we've done our best by reporting the error(s),
    // the caller should handle an undefined result.
    return undefined
  }
}
declare module '@ember/service' {
  interface Registry {
    recentWriting: RecentWritingService
  }
}
